/**
 * Listens for HTTP responses on endpoints that mutate block-level counts
 * and dispatches a single debounced `blocksRefreshMetaRequest` per burst.
 *
 * Why an interceptor instead of per-component dispatches:
 *   - 23 block types each have multiple mutation handlers (delete one, delete all,
 *     change status, etc.). Wiring dispatches into every handler is fragile —
 *     someone always forgets. An interceptor scoped to known mutating endpoints
 *     gives complete coverage with zero per-component cost.
 *
 * Coverage rule:
 *   - URL contains one of MUTATION_PATTERNS, AND
 *   - method is POST, PUT, PATCH, or DELETE, AND
 *   - response status is in 2xx
 *
 * Reads are explicitly skipped — only writes invalidate.
 */

const MUTATION_PATTERNS = [
  // Form responses
  '/audience/forms',
  // Generic profile orders (Shop, Service, Donation, Tip, Coupon, Course, Membership,
  // Event, Digital, Appointment, SingleProduct, File)
  '/orders',
  // Block-specific tables
  '/booking-blocks/',
  '/ama-blocks/',
  '/unlock-blocks/',
  // DataCollect submissions
  '/audience/subscribers',
];

const DEBOUNCE_MS = 500;

let debounceTimer = null;

const matchesMutation = (config) => {
  const url = config?.url || '';
  const method = (config?.method || '').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false;
  return MUTATION_PATTERNS.some((p) => url.includes(p));
};

export const installBlockMetaInvalidator = (axiosInstance, onInvalidate) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      try {
        if (matchesMutation(response.config)) {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            debounceTimer = null;
            onInvalidate();
          }, DEBOUNCE_MS);
        }
      } catch {
        // Never break the response chain because of metadata invalidation.
      }
      return response;
    },
    (error) => Promise.reject(error),
  );
};
